import { Avatar, Box, Chip, TextField, makeStyles } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { useContext, useEffect, useState } from "react";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getBackendUrl } from "../../config";

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    minWidth: 0,
    "& .MuiAutocomplete-inputRoot": {
      flexWrap: "nowrap !important",
      overflow: "hidden",
      alignItems: "center",
      maxHeight: 40,
    },
    "& .MuiAutocomplete-tag": {
      maxWidth: "calc(100% - 36px)",
      margin: "1px 2px 1px 0 !important",
    },
    "& .MuiChip-root": {
      height: 20,
      maxWidth: "100%",
    },
    "& .MuiChip-label": {
      paddingLeft: 4,
      paddingRight: 4,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      display: "flex",
      alignItems: "center",
    },
  },
  optionRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    minWidth: 0,
    width: "100%",
  },
  optionName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  chipLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    maxWidth: "100%",
    overflow: "hidden",
  },
  chipName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 72,
    fontSize: "0.62rem",
  },
  limitTag: {
    height: 20,
    fontSize: "0.62rem",
    backgroundColor: "rgba(0,0,0,0.08)",
    margin: "1px 0 !important",
  },
  avatar: {
    width: 16,
    height: 16,
    fontSize: "0.55rem",
    flexShrink: 0,
  },
}));

const getUserAvatarUrl = (user, companyId) => {
  if (!user?.profileImage || !companyId) return "";
  return `${getBackendUrl()}/public/company${companyId}/user/${user.profileImage}`;
};

const UserAvatar = ({ user, companyId, className }) => (
  <Avatar
    src={getUserAvatarUrl(user, companyId)}
    alt={user?.name || ""}
    className={className}
  >
    {(user?.name || "?").charAt(0).toUpperCase()}
  </Avatar>
);

const renderUserChipLabel = (option, companyId, classes) => (
  <span className={classes.chipLabel}>
    <UserAvatar user={option} companyId={companyId} className={classes.avatar} />
    <span className={classes.chipName}>{option.name}</span>
  </span>
);

export function UsersFilter({ onFiltered, initialUsers }) {
  const classes = useStyles();
  const { user: authUser } = useContext(AuthContext);
  const companyId = authUser?.companyId;
  const [users, setUsers] = useState([]);
  const [selecteds, setSelecteds] = useState([]);

  useEffect(() => {
    async function fetchData() {
      await loadUsers();
    }
    fetchData();
  }, []);

  useEffect(() => {
    setSelecteds([]);
    if (
      Array.isArray(initialUsers) &&
      Array.isArray(users) &&
      users.length > 0
    ) {
      onChange(initialUsers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUsers, users]);

  const loadUsers = async () => {
    try {
      const { data } = await api.get(`/users/list`);
      const userList = data.map((u) => ({
        id: u.id,
        name: u.name,
        profileImage: u.profileImage,
      }));
      setUsers(userList);
    } catch (err) {
      toastError(err);
    }
  };

  const onChange = async (value) => {
    setSelecteds(value);
    onFiltered(value);
  };

  return (
    <Box className={classes.root}>
      <Autocomplete
        multiple
        size="small"
        options={users}
        value={selecteds}
        onChange={(e, v) => onChange(v)}
        getOptionLabel={(option) => option.name || ""}
        getOptionSelected={(option, value) =>
          option?.id === value?.id ||
          option?.name?.toLowerCase() === value?.name?.toLowerCase()
        }
        renderOption={(option) => (
          <div className={classes.optionRow}>
            <UserAvatar user={option} companyId={companyId} className={classes.avatar} />
            <span className={classes.optionName}>{option.name}</span>
          </div>
        )}
        renderTags={(value, getUserProps) => {
          if (!value.length) return null;

          if (value.length === 1) {
            const option = value[0];
            return [
              <Chip
                key={option.id || option.name}
                variant="outlined"
                style={{
                  backgroundColor: "#bfbfbf",
                  color: "white",
                }}
                label={renderUserChipLabel(option, companyId, classes)}
                {...getUserProps({ index: 0 })}
                size="small"
              />,
            ];
          }

          return [
            <Chip
              key="users-summary"
              className={classes.limitTag}
              size="small"
              label={
                <span className={classes.chipLabel}>
                  <UserAvatar
                    user={value[0]}
                    companyId={companyId}
                    className={classes.avatar}
                  />
                  <span className={classes.chipName}>
                    {value.length} usuários
                  </span>
                </span>
              }
              onDelete={() => onChange([])}
            />,
          ];
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={selecteds.length ? "" : "Usuários"}
          />
        )}
      />
    </Box>
  );
}
